import { Link } from "react-router-dom";
import { brand, socialLinks } from "@/data/mockData";

const quickLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Me" },
  { to: "/services", label: "Services" },
  { to: "/listings", label: "Listings" },
  { to: "/framework", label: "Framework" },
  { to: "/results", label: "Proven Results" },
  { to: "/testimonials", label: "Testimonials" },
  { to: "/awards", label: "Awards" },
  { to: "/calculators", label: "Calculators" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Footer() {
  return (
    <footer className="bg-white border-t border-gray-100" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <img
                src={brand.logoUrl}
                alt="Johnny Tan logo"
                className="h-12 w-12 rounded-full object-cover"
                loading="lazy"
              />
              <div>
                <div className="font-serif font-semibold text-xl text-[#0a2540] leading-tight">
                  Johnny Tan. <span className="font-serif">陈健盛</span>
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#0a2540]/70 font-semibold mt-0.5">
                  {brand.title}
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed max-w-md mb-5">
              Strategic property advisory across Singapore's residential market — every plan tailored to your life stage, goals, and timeline.
            </p>
            <ul
              className="flex flex-wrap gap-2 mb-6 max-w-md"
              data-testid="footer-positioning-tags"
              aria-label="Areas of expertise"
            >
              {brand.positioning.split("|").map((part) => (
                <li key={part.trim()}>
                  <span className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#0a2540]/5 border border-[#0a2540]/10 text-[11px] sm:text-xs text-[#0a2540] font-medium tracking-tight whitespace-nowrap">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#ffde59] shrink-0" aria-hidden="true" />
                    {part.trim()}
                  </span>
                </li>
              ))}
            </ul>
            <a
              href={brand.domainUrl}
              className="inline-flex items-center gap-2 text-sm font-medium text-[#0a2540] link-underline"
              data-testid="footer-domain-link"
            >
              {brand.domain}
              <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
            </a>
            <div className="mt-6 flex flex-wrap items-center gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="h-10 w-10 inline-flex items-center justify-center border border-gray-200 text-slate-600 hover:bg-[#ffde59] hover:text-[#023e8a] hover:border-[#ffde59] rounded-full transition-all"
                >
                  <i className={s.icon} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-4">
            <h4 className="font-serif text-[#0a2540] text-base mb-5">Sitemap</h4>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-slate-600 hover:text-[#0a2540] link-underline"
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="font-serif text-[#0a2540] text-base mb-5">Get in Touch</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={`tel:${brand.phoneRaw}`} className="flex items-center gap-2.5 text-slate-600 hover:text-[#0a2540]" data-testid="footer-phone-link">
                  <i className="fa-solid fa-phone text-[#0a2540]" aria-hidden="true" />
                  {brand.phone}
                </a>
              </li>
              <li>
                <a href={brand.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-slate-600 hover:text-[#0a2540]" data-testid="footer-whatsapp-link">
                  <i className="fa-brands fa-whatsapp text-[#0a2540]" aria-hidden="true" />
                  WhatsApp
                </a>
              </li>
              <li>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-4 py-2.5 text-xs font-semibold rounded-full hover:shadow-md transition-all mt-2" data-testid="footer-schedule-btn">
                  Schedule an Appointment
                </Link>
              </li>
            </ul>
            <div className="mt-6 text-[11px] text-slate-400 space-y-0.5 font-mono">
              <div>CEA: {brand.cea}</div>
            </div>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-gray-100 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <p className="text-xs text-slate-400 max-w-3xl leading-relaxed">
            Disclaimer: Information on this website is for general property advisory and informational purposes only. Please consult Johnny Tan directly for personalised advice.
          </p>
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Johnny Tan · {brand.domain}
          </p>
        </div>
      </div>
    </footer>
  );
}
